import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { migrateLegacyAuthPersistence } from '@/lib/mobile/auth-persistence';
import { hideSplashScreen } from '@/lib/mobile/splash-screen';
import { isAuthError } from '@/lib/supabase-utils';
import { isAuthCircuitOpen } from '@/lib/auth-circuit';
import { verifyAuthThenBreak } from '@/lib/auth-circuit-actions';
import { isDesktopBridgeCompatible, isDesktopPlatform } from '@/lib/desktop/bridge';
import { prepareQueryPersistenceStorage, shouldDehydrateQuery } from '@/lib/query-persistence';
import { isPlaintextCacheDisallowed } from '@/lib/query-persistence-platform';
import { useDeepLinkAuth } from '@/hooks/use-deep-link-auth';
import { useLanguageSync } from '@/hooks/use-language-settings';
import { Toaster } from '@/components/shared/toast';
import { FullScreenBrandLoader } from '@/components/shared/full-screen-brand-loader';
import { MobileBootScreen } from '@/components/mobile/MobileBootScreen';
import { ShareAccessRealtimeProvider } from '@/hooks/share-access/share-access-realtime-provider';
import { TasksSyncRealtimeMount } from '@/hooks/use-tasks-sync-realtime';
import { ExternalSenderGrantRealtimeMount } from '@/hooks/external-email-send/use-sender-grant-revocation-realtime';
import { DesktopIntegrationMount } from '@/hooks/use-desktop-integration';
import { bootPaintedOrTimeout } from '@/lib/mobile/boot-paint';

import { i18nReady } from './i18n';
import ProtectedRoute from './components/auth/protected-route';
import BrandHeader from './components/auth/brand-header';
import AuthProvider from './contexts/auth-context';
import { CacheScopeProvider } from './contexts/cache-scope-provider';
import { OrganizationProvider } from './contexts/organization-provider';
import Login from './pages/login';
import MagicLink from './pages/magic-link';
import ResetPassword from './pages/reset-password';
import UpdatePassword from './pages/update-password';
import NotFound from './pages/NotFound';
import AuthCallback from './pages/auth-callback';
import RequestAccess from './pages/request-access';
import { ErrorFallback } from './components/ErrorFallback';
import { IntercomProvider } from './contexts/intercom-context';
/* eslint-disable max-lines -- Route registration entrypoint; breadth is intentional. */
import { useNotificationTitle } from './hooks/use-document-title';
import { useNotificationSoundMonitor } from './hooks/use-notification-sound-monitor';
import { isNativePlatform } from './lib/mobile/platform';

// The whole desktop surface (V1 dashboard layout, print, admin console) is
// pulled off the boot path. On the native (Capacitor) build,
// `VITE_NATIVE_SHELL === '1'` is inlined by Vite's define replacement, so the
// `renderDesktopRoutes()` branch below is statically dead and Rollup drops this
// module — the admin/print code never reaches the native webview. The desktop
// `/v2/*` routes deliberately stay in App.tsx: mobile deep links resolve them.
const Welcome = lazy(async () => await import('./pages/welcome'));
const Onboarding = lazy(async () => await import('./pages/onboarding'));
// Anonymous external share viewer - guests only, stays out of the main bundle.
const ShareThreadPage = lazy(async () => await import('./pages/share-thread-page'));
const PublicAdPage = lazy(async () => await import('./pages/public-ad-page'));

// Ternary (not a plain `lazy(...)`) so the `import('./app-desktop-routes')` lives
// only in the else branch. On the native build `VITE_NATIVE_SHELL === '1'` is
// inlined, the branch is statically `null`, and Rollup eliminates the import —
// dropping app-desktop-routes and every admin/print chunk it pulls in. A
// module-level `const … = lazy(() => import(…))` would be retained by Rollup
// (the `lazy()` call is not provably side-effect-free) and keep the chunk.
const DesktopRoutes =
  import.meta.env.VITE_NATIVE_SHELL === '1'
    ? null
    : lazy(async () => await import('./app-desktop-routes'));

// V2 pages are route-level lazy splits, each its own chunk. Direct per-page
// default imports stay typed; a `./pages/v2` barrel has no index here and would
// resolve to `any`. Still one shared Suspense boundary (V2RouteFallbackBoundary).
const V2CorrespondencePage = lazy(async () => await import('./pages/v2/v2-correspondence-page'));
const V2Layout = lazy(async () => {
  const module = await import('./components/v2-production/layout');
  return { default: module.V2Layout };
});
const V2CrmPage = lazy(async () => await import('./pages/v2/v2-crm-page'));
const V2CrmDetailPage = lazy(async () => await import('./pages/v2/v2-crm-detail-page'));
const V2OrganisationDetailPage = lazy(
  async () => await import('./pages/v2/v2-organisation-detail-page'),
);
const V2DashboardPage = lazy(async () => await import('./pages/v2/v2-dashboard-page'));
const V2KnowledgePage = lazy(async () => await import('./pages/v2/v2-knowledge-page'));
const V2PulseDocumentPage = lazy(async () => await import('./pages/v2/v2-pulse-document-page'));
const V2NotificationsPage = lazy(async () => await import('./pages/v2/v2-notifications-page'));
const V2PulseAiPage = lazy(async () => await import('./pages/v2/v2-pulse-ai-page'));
const V2ReportingPage = lazy(async () => await import('./pages/v2/v2-reporting-page'));
const V2SettingsPage = lazy(async () => await import('./pages/v2/v2-settings-page'));
const V2TasksPage = lazy(async () => await import('./pages/v2/v2-tasks-page'));

const V2ExplorerPage = lazy(async () => await import('./pages/v2/v2-explorer-page'));
const V2QuickDraftsPage = lazy(async () => await import('./pages/v2/v2-quick-drafts-page'));
const V2DiscoverPage = lazy(async () => await import('./pages/v2/v2-discover-page'));
const V2AutomationsPage = lazy(async () => await import('./pages/v2/v2-automations-page'));
const V2WebsitesPage = lazy(async () => await import('./pages/v2/v2-websites-page'));
const V2CampaignsPage = lazy(async () => await import('./pages/v2/v2-campaigns-page'));
const V2CampaignBuilderPage = lazy(async () => await import('./pages/v2/v2-campaign-builder-page'));
const V2AdsExplorerPage = lazy(async () => await import('./pages/v2/v2-ads-explorer-page'));
const V2AdsAdvertiserPage = lazy(async () => await import('./pages/v2/v2-ads-advertiser-page'));
const V2CreativePage = lazy(async () => await import('./pages/v2/v2-creative-page'));
const V2ClipStudioPage = lazy(async () => await import('./pages/v2/v2-clip-studio-page'));
const V2ComposerWorkspacePage = lazy(
  async () => await import('./pages/v2/v2-composer-workspace-page'),
);
const MFAVerify = lazy(async () => await import('./pages/mfa-verify'));
const DeepLinkRouter = lazy(async () => {
  const module = await import('./components/mobile/deep-link-router');
  return { default: module.DeepLinkRouter };
});
const PushRegistrar = lazy(async () => {
  const module = await import('./components/mobile/push-registrar');
  return { default: module.PushRegistrar };
});
const NativeResumeManager = lazy(async () => {
  const module = await import('./components/mobile/native-resume-manager');
  return { default: module.NativeResumeManager };
});
const LazyMobileShell = lazy(async () => {
  const module = await import('./components/mobile/MobileShell');
  return { default: module.MobileShell };
});
const LazyMobileHome = lazy(async () => await import('./pages/mobile/mobile-home'));
const LazyMobileNotifications = lazy(
  async () => await import('./pages/mobile/mobile-notifications'),
);
const LazyMobileInbox = lazy(async () => await import('./pages/mobile/mobile-inbox'));
const LazyMobileTasks = lazy(async () => await import('./pages/mobile/mobile-tasks'));

// Suppress TanStack Query CancelledError warnings in development
// These are expected when queries are cancelled due to filter changes or component unmounting
// Note: The logger option was removed in TanStack Query v5, so we override console.error instead
const originalConsoleError = console.error;
const getErrorMessage = (arg: unknown): string => {
  if (arg instanceof Error) {
    return arg.message;
  }
  if (typeof arg === 'string') {
    return arg;
  }
  return '';
};
console.error = (...args: unknown[]) => {
  const message = getErrorMessage(args[0]);
  if (
    message.includes('dehydrated as pending') ||
    message.includes('CancelledError') ||
    message.includes('ended up rejecting')
  ) {
    return; // Suppress these expected errors
  }
  originalConsoleError.apply(console, args);
};

const MAX_QUERY_RETRIES = 2;

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (isAuthError(error)) {
        verifyAuthThenBreak(queryClient, 'query_auth_error');
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isAuthError(error)) {
        verifyAuthThenBreak(queryClient, 'mutation_auth_error');
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Keep data fresh for longer to avoid frequent refetches
      staleTime: 10 * 60 * 1000, // 10 minutes
      // Retain cache for a day so it can be restored after reloads
      gcTime: 24 * 60 * 60 * 1000, // 24 hours
      // Reduce surprise background refetching
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        // Circuit breaker open or auth error: stop all retries
        if (isAuthCircuitOpen() || isAuthError(error)) {
          return false;
        }
        return failureCount < MAX_QUERY_RETRIES;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// Browser reloads retain an allowlisted query cache. Desktop and native disable
// the plaintext cache entirely; on both, a native session and a long-lived
// renderer already cover its lifecycle, and neither shell's storage is a browser
// profile the user can clear.
const queryPersistenceStorage =
  globalThis.window === undefined
    ? undefined
    : prepareQueryPersistenceStorage(globalThis.window.localStorage, isPlaintextCacheDisallowed());
const localStoragePersister =
  queryPersistenceStorage === undefined
    ? undefined
    : createSyncStoragePersister({ storage: queryPersistenceStorage });

if (localStoragePersister) {
  // `persistQueryClient` returns `[unsubscribe, restorePromise]`. Chain
  // `.catch(() => undefined)` so boot doesn't block on localStorage quota
  // errors — satisfies `no-floating-promises` without `void`, which
  // `sonarjs/void-use` disallows.
  const [, restorePromise] = persistQueryClient({
    queryClient,
    persister: localStoragePersister,
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
    buster: 'v34', // Evict snapshots persisted before viewer-scoped evidence keys (ADR 0091).
    dehydrateOptions: {
      // Skip-list lives in @/lib/query-persistence so it can be unit-tested.
      shouldDehydrateQuery,
      // TanStack's default persists PAUSED mutations, whose variables carry the
      // very payloads the query skip-list exists to keep out of localStorage
      // (draft bodies, passkey friendly names). Nothing here registers mutation
      // defaults, so a persisted mutation could never be resumed anyway — it is
      // pure leak surface on a shared machine (PUB-894 class).
      shouldDehydrateMutation: () => false,
    },
  });
  restorePromise.catch(() => {
    /* intentional swallow */
  });
}

// Lightweight fallback for the shared V2 lazy boundary. Kept dependency-free and
// inline so it stays on the boot path at near-zero cost — pulling a real page
// skeleton here would defeat the point of lazifying the V2 chunk.
const V2RouteFallback = () => (
  <div
    aria-hidden
    className="flex-1 animate-pulse bg-[var(--color-bg-quaternary)]/40"
    data-testid="v2-route-fallback"
  />
);

// Pathless layout route: one Suspense boundary shared by every lazy V2 page so
// the first navigation shows the V2Layout chrome + a skeleton, not a blank flash.
const V2RouteFallbackBoundary = () => (
  <Suspense fallback={<V2RouteFallback />}>
    <Outlet />
  </Suspense>
);

// Inner component that uses the notification title hook
// Must be inside AuthProvider and OrganizationProvider to access context
const AppRoutes = () => {
  // Update browser tab title with unread notification count
  useNotificationTitle();

  // Play sound when new notifications arrive (singleton — one chime, not N)
  useNotificationSoundMonitor();

  // Handle OAuth deep link callbacks on native platforms
  useDeepLinkAuth();

  // Reconcile the active UI language with the resolved profile/org preference
  // once auth + org context are known (premortem R6).
  useLanguageSync();

  const mountNativeRoutes = isNativePlatform();

  return (
    <>
      <DesktopIntegrationMount />
      <Toaster />
      {mountNativeRoutes ? (
        <Suspense fallback={null}>
          <PushRegistrar />
          <NativeResumeManager />
          <DeepLinkRouter />
        </Suspense>
      ) : null}
      <Routes>
        <Route element={<Navigate replace to="/" />} path="/landing" />
        <Route element={<Navigate replace to="/auth/login" />} path="/login" />
        <Route element={<Navigate replace to="/auth/login" />} path="/auth" />
        <Route element={<Login />} path="/auth/login" />
        <Route element={<ResetPassword />} path="/auth/reset-password" />
        <Route element={<MagicLink />} path="/auth/magic-link" />
        <Route element={<UpdatePassword />} path="/auth/update-password" />
        <Route element={<AuthCallback />} path="/auth/callback" />
        <Route
          element={
            <Suspense fallback={mountNativeRoutes ? <MobileBootScreen /> : null}>
              <MFAVerify />
            </Suspense>
          }
          path="/auth/mfa-verify"
        />
        <Route element={<Welcome />} path="/welcome" />
        <Route element={<Onboarding />} path="/onboarding" />
        <Route element={<RequestAccess />} path="/request-access" />
        <Route
          element={
            <Suspense fallback={mountNativeRoutes ? <MobileBootScreen /> : null}>
              <ShareThreadPage />
            </Suspense>
          }
          path="/share/:token"
        />
        <Route
          element={
            <Suspense fallback={mountNativeRoutes ? <MobileBootScreen /> : null}>
              <PublicAdPage />
            </Suspense>
          }
          path="/ad/:token"
        />
        {mountNativeRoutes ? (
          <Route element={<ProtectedRoute />}>
            <Route
              element={
                <Suspense fallback={<MobileBootScreen />}>
                  <LazyMobileShell />
                </Suspense>
              }
              path="/m"
            >
              <Route index element={<Navigate replace to="/m/home" />} />
              <Route element={<LazyMobileHome />} path="home" />
              <Route element={<LazyMobileNotifications />} path="notifications" />
              <Route element={<LazyMobileTasks />} path="tasks" />
              <Route element={<LazyMobileInbox />} path="inbox" />
            </Route>
          </Route>
        ) : null}

        {mountNativeRoutes ? (
          <Route element={<Navigate replace to="/m/home" />} path="*" />
        ) : (
          <>
            {/* The workspace. Canonical (unversioned), /v2 compatibility, and historical */}
            {/* alias URLs all register as exact case-sensitive matcher leaves beneath ONE */}
            {/* pathless V2Layout route, so history moving between two presentations of the */}
            {/* same destination never remounts the layout or tab state. On desktop and */}
            {/* mobile web V2Layout renders no <Outlet> - page content comes from */}
            {/* TabContentRenderer inside the shell chrome - so a redirect declared as a */}
            {/* leaf below would match but never execute. Presentation normalisation */}
            {/* (canonical or alias input replaced by its /v2 twin) happens in */}
            {/* WorkspacePresentationGate, rendered inside the layout's provider tree. */}
            {/* Leaves still carry their destination's page element for shells that do */}
            {/* render an <Outlet> (the narrow-native MobileShell). These routes stay in */}
            {/* App.tsx (not the native-excluded desktop subtree) because the mobile build */}
            {/* must still resolve /v2 deep links. */}
            <Route element={<ProtectedRoute />}>
              <Route element={<V2Layout />}>
                <Route element={<V2RouteFallbackBoundary />}>
                  {/* The permanent /v2 compatibility presentation, including aliases that */}
                  {/* only ever existed under /v2 (/v2/crm, /v2/organisations, the */}
                  {/* pre-rename /v2/reporting). */}
                  <Route caseSensitive element={<V2DashboardPage />} path="/v2" />
                  <Route
                    caseSensitive
                    element={<V2CorrespondencePage />}
                    path="/v2/correspondence"
                  />
                  <Route
                    caseSensitive
                    element={<V2CorrespondencePage />}
                    path="/v2/correspondence/catch-up"
                  />
                  <Route caseSensitive element={<V2CrmPage />} path="/v2/people" />
                  <Route caseSensitive element={<V2CrmPage />} path="/v2/people/organisations" />
                  <Route
                    caseSensitive
                    element={<V2OrganisationDetailPage />}
                    path="/v2/people/organisations/:organisationId"
                  />
                  <Route caseSensitive element={<V2CrmDetailPage />} path="/v2/people/:id" />
                  <Route caseSensitive element={<V2CrmPage />} path="/v2/organisations" />
                  <Route caseSensitive element={<V2CrmPage />} path="/v2/crm" />
                  <Route caseSensitive element={<V2CrmPage />} path="/v2/crm/organisations" />
                  <Route
                    caseSensitive
                    element={<V2OrganisationDetailPage />}
                    path="/v2/crm/organisations/:organisationId"
                  />
                  <Route caseSensitive element={<V2CrmDetailPage />} path="/v2/crm/:id" />
                  <Route caseSensitive element={<V2KnowledgePage />} path="/v2/knowledge" />
                  <Route
                    caseSensitive
                    element={<V2PulseDocumentPage />}
                    path="/v2/knowledge/documents/:documentId"
                  />
                  <Route caseSensitive element={<V2PulseAiPage />} path="/v2/pulse-ai" />
                  <Route caseSensitive element={<V2NotificationsPage />} path="/v2/notifications" />
                  <Route caseSensitive element={<V2SettingsPage />} path="/v2/settings" />
                  <Route
                    caseSensitive
                    element={<V2SettingsPage />}
                    path="/v2/settings/email-accounts"
                  />
                  <Route
                    caseSensitive
                    element={<V2SettingsPage />}
                    path="/v2/settings/sending-domains"
                  />
                  <Route caseSensitive element={<V2TasksPage />} path="/v2/tasks" />
                  <Route caseSensitive element={<V2TasksPage />} path="/v2/tasks/archive" />
                  <Route caseSensitive element={<V2TasksPage />} path="/v2/tasks/:id" />
                  <Route caseSensitive element={<V2ReportingPage />} path="/v2/analytics" />
                  <Route caseSensitive element={<V2ReportingPage />} path="/v2/reporting" />
                  <Route caseSensitive element={<V2ReportingPage />} path="/v2/reporting/*" />
                  <Route caseSensitive element={<V2ExplorerPage />} path="/v2/explorer" />
                  <Route caseSensitive element={<V2AdsExplorerPage />} path="/v2/ads-explorer" />
                  {/* Advertiser drill-down pages (plan U6). Standalone deep-link routes, NOT */}
                  {/* nav tabs - reached by clicking a card, so they carry a shareable per- */}
                  {/* advertiser URL and are absent from v2-routes.ts / tab-content-renderer. */}
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/v2/ads/advertiser/:metaPageId"
                  />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/v2/ads/entity/:entityId"
                  />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/v2/ads/disclaimer/:hash"
                  />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/v2/ads/party/:blocSlug"
                  />
                  <Route caseSensitive element={<V2CreativePage />} path="/v2/creative" />
                  <Route caseSensitive element={<V2ClipStudioPage />} path="/v2/clip-studio" />
                  <Route
                    caseSensitive
                    element={<V2ComposerWorkspacePage />}
                    path="/v2/clip-studio/projects/:projectId/compositions/:compositionId"
                  />
                  <Route caseSensitive element={<V2QuickDraftsPage />} path="/v2/quick-drafts" />
                  <Route caseSensitive element={<V2DiscoverPage />} path="/v2/discover" />
                  <Route caseSensitive element={<V2AutomationsPage />} path="/v2/automations" />
                  <Route caseSensitive element={<V2CampaignsPage />} path="/v2/campaigns" />
                  <Route caseSensitive element={<V2WebsitesPage />} path="/v2/websites" />
                  <Route
                    caseSensitive
                    element={<V2CampaignBuilderPage />}
                    path="/v2/campaigns/:id"
                  />
                  {/* The canonical presentation, one leaf per /v2 twin above. */}
                  <Route caseSensitive element={<V2DashboardPage />} path="/" />
                  <Route caseSensitive element={<V2CorrespondencePage />} path="/correspondence" />
                  <Route caseSensitive element={<V2CrmPage />} path="/people" />
                  <Route caseSensitive element={<V2CrmPage />} path="/people/organisations" />
                  <Route
                    caseSensitive
                    element={<V2OrganisationDetailPage />}
                    path="/people/organisations/:organisationId"
                  />
                  <Route caseSensitive element={<V2CrmDetailPage />} path="/people/:id" />
                  <Route caseSensitive element={<V2KnowledgePage />} path="/knowledge" />
                  <Route
                    caseSensitive
                    element={<V2PulseDocumentPage />}
                    path="/knowledge/documents/:documentId"
                  />
                  <Route caseSensitive element={<V2PulseAiPage />} path="/pulse-ai" />
                  <Route caseSensitive element={<V2NotificationsPage />} path="/notifications" />
                  <Route caseSensitive element={<V2SettingsPage />} path="/settings" />
                  <Route caseSensitive element={<V2TasksPage />} path="/tasks" />
                  <Route caseSensitive element={<V2TasksPage />} path="/tasks/:id" />
                  <Route caseSensitive element={<V2ReportingPage />} path="/analytics" />
                  <Route caseSensitive element={<V2ExplorerPage />} path="/explorer" />
                  <Route caseSensitive element={<V2AdsExplorerPage />} path="/ads-explorer" />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/ads/advertiser/:metaPageId"
                  />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/ads/entity/:entityId"
                  />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/ads/disclaimer/:hash"
                  />
                  <Route
                    caseSensitive
                    element={<V2AdsAdvertiserPage />}
                    path="/ads/party/:blocSlug"
                  />
                  <Route caseSensitive element={<V2CreativePage />} path="/creative" />
                  <Route caseSensitive element={<V2ClipStudioPage />} path="/clip-studio" />
                  <Route
                    caseSensitive
                    element={<V2ComposerWorkspacePage />}
                    path="/clip-studio/projects/:projectId/compositions/:compositionId"
                  />
                  <Route caseSensitive element={<V2QuickDraftsPage />} path="/quick-drafts" />
                  <Route caseSensitive element={<V2DiscoverPage />} path="/discover" />
                  <Route caseSensitive element={<V2AutomationsPage />} path="/automations" />
                  <Route caseSensitive element={<V2CampaignsPage />} path="/campaigns" />
                  <Route caseSensitive element={<V2WebsitesPage />} path="/websites" />
                  <Route caseSensitive element={<V2CampaignBuilderPage />} path="/campaigns/:id" />
                  {/* Unversioned legacy aliases - installed PWA shortcuts, stored */}
                  {/* notification action_urls, and bookmarks depend on these exact inputs. */}
                  {/* The gate rewrites them to their /v2 twin, carrying the destination's */}
                  {/* identity query (e.g. /settings/email-accounts lands on */}
                  {/* /v2/settings?tab=email-settings) alongside the preserved search/hash. */}
                  <Route caseSensitive element={<V2DashboardPage />} path="/dashboard" />
                  <Route
                    caseSensitive
                    element={<V2CorrespondencePage />}
                    path="/correspondence/catch-up"
                  />
                  <Route caseSensitive element={<V2CrmPage />} path="/constituents" />
                  <Route caseSensitive element={<V2CrmPage />} path="/constituents/organisations" />
                  <Route caseSensitive element={<V2CrmDetailPage />} path="/constituents/:id" />
                  <Route
                    caseSensitive
                    element={<V2SettingsPage />}
                    path="/settings/email-accounts"
                  />
                  <Route
                    caseSensitive
                    element={<V2SettingsPage />}
                    path="/settings/sending-domains"
                  />
                  <Route caseSensitive element={<V2SettingsPage />} path="/user/settings" />
                  <Route caseSensitive element={<V2TasksPage />} path="/tasks/archive" />
                  <Route caseSensitive element={<V2CampaignBuilderPage />} path="/broadcasts/:id" />
                </Route>
              </Route>
            </Route>

            {/* Desktop-only surface (print, V1 dashboard layout, admin console) plus its */}
            {/* own not-found fallback, mounted lazily on the `/*` splat. `DesktopRoutes` is */}
            {/* null in the native build (see its declaration), so Rollup has already */}
            {/* dropped app-desktop-routes and every admin/print chunk; the plain NotFound */}
            {/* splat is kept instead. */}
            {DesktopRoutes ? (
              <Route
                element={
                  // Shared full-screen loader rather than `null`: this boundary is
                  // the first wait on the desktop entry path and the guard inside
                  // paints the same loader, so a blank frame here would split one
                  // continuous wait into two logo flashes.
                  <Suspense fallback={<FullScreenBrandLoader />}>
                    <DesktopRoutes />
                  </Suspense>
                }
                path="*"
              />
            ) : (
              <Route element={<NotFound />} path="*" />
            )}
          </>
        )}
      </Routes>
    </>
  );
};

// Web-only chrome. These lazy components are stable module-level references, so
// the dynamic import fires only when the element first renders — which the
// gates below defer to browser idle time on web and suppress entirely on
// native. Intercom is therefore never bundled into the native webview boot path.
const LazyIntercomMessenger = lazy(async () => {
  const module = await import('./components/Intercom');
  return { default: module.IntercomMessenger };
});
const LazyCommandMenu = lazy(async () => {
  const module = await import('./components/shared/CommandMenu');
  return { default: module.CommandMenu };
});

// True only on web, and only after the browser goes idle — so the ⌘K menu and
// Intercom never compete with first paint, and never load at all in the native
// webview (`isNativePlatform()` short-circuits before scheduling the import).
function useIdleWebReady(): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isNativePlatform()) {
      return;
    }

    const idle =
      globalThis.requestIdleCallback ??
      ((callback: () => void) => globalThis.setTimeout(callback, 200));
    const cancelIdle = globalThis.cancelIdleCallback ?? globalThis.clearTimeout;

    const handle = idle(() => setReady(true));

    return () => {
      cancelIdle(handle as number);
    };
  }, []);

  return ready;
}

// Anonymous share surfaces carry a bearer token in the path; the third-party
// Intercom SDK must not mount there and capture it (an anonymous guest never boots
// it — there's no userId — but an authed user opening their own share link would).
const PUBLIC_SHARE_PATH_PATTERN = /^\/(ad|share)\//;

const DeferredIntercomMessenger = () => {
  const ready = useIdleWebReady();
  const { pathname } = useLocation();
  if (!ready || PUBLIC_SHARE_PATH_PATTERN.test(pathname)) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <LazyIntercomMessenger />
    </Suspense>
  );
};

const DeferredCommandMenu = () => {
  const ready = useIdleWebReady();
  return ready ? (
    <Suspense fallback={null}>
      <LazyCommandMenu />
    </Suspense>
  ) : null;
};

const DesktopUpdateRequired = () => {
  const { t } = useTranslation('auth');

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-secondary p-6">
      <section
        aria-labelledby="desktop-update-required-heading"
        className="w-full max-w-md rounded-2xl border border-secondary bg-surface p-8 text-center shadow-lg"
      >
        <BrandHeader className="mb-6 justify-center" />
        <h1
          className="text-display-xs font-semibold text-primary"
          id="desktop-update-required-heading"
        >
          {t('desktopCompatibility.heading')}
        </h1>
        <p className="mt-3 text-md text-tertiary">{t('desktopCompatibility.description')}</p>
      </section>
    </main>
  );
};

function useAppBootstrap(): void {
  // Hide splash screen when app mounts (native platforms only).
  // Also fire the one-time migration that purges any legacy plaintext
  // Supabase session left in @capacitor/preferences by pre-Keychain
  // builds, so a user who upgrades without ever logging out does not
  // keep a plaintext refresh token in UserDefaults.
  useEffect(() => {
    // Hold the native splash until BOTH the active language resolves (so the
    // first paint is already localized — no English flash that flips to
    // French, premortem R6) AND the boot canvas has committed a frame (so the
    // lift never exposes an unpainted webview — the white-flash class).
    // bootPaintedOrTimeout is time-bounded, so a path that never mounts the
    // boot screen cannot hold the splash. hideSplashScreen swallows its own
    // errors; allSettled guards an i18n init rejection so the splash always
    // lifts.
    // Web has no splash to hold, so the paint gate is native-only (the flag
    // is inlined, making this branch compile-time on both builds).
    const paintGate =
      import.meta.env.VITE_NATIVE_SHELL === '1' ? bootPaintedOrTimeout() : Promise.resolve();
    Promise.allSettled([i18nReady, paintGate])
      .then(async () => {
        await hideSplashScreen();
      })
      .catch(() => {
        /* hideSplashScreen swallows its own errors; this only satisfies the chain */
      });
    (async () => {
      await migrateLegacyAuthPersistence();
    })().catch(() => {
      /* cleanup is best effort; the compatibility gate still blocks auth */
    });
  }, []);
}

const AppRuntime = () => {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <IntercomProvider>
            <BrowserRouter>
              <DeferredIntercomMessenger />
              <DeferredCommandMenu />
              <OrganizationProvider>
                <CacheScopeProvider>
                  <ShareAccessRealtimeProvider>
                    <TasksSyncRealtimeMount />
                    <ExternalSenderGrantRealtimeMount />
                    <AppRoutes />
                  </ShareAccessRealtimeProvider>
                </CacheScopeProvider>
              </OrganizationProvider>
            </BrowserRouter>
          </IntercomProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

const App = () => {
  useAppBootstrap();
  if (isDesktopPlatform() && !isDesktopBridgeCompatible()) {
    return <DesktopUpdateRequired />;
  }
  return <AppRuntime />;
};

export default App;
